'use client';

/**
 * DashboardFooter — the dashboard-native footer for the Descout shell.
 *
 * F6 grew this from the slim Slice-1 band into a fuller-but-still-dashboard link grid:
 * every long-tail / orphan page is reachable from here ("they live in the footer, not
 * the rail"). It stays on Descout tokens (ink/surface/divider/parchment-dim/scout-gold)
 * with a terminal feel — NOT the old tall marketing `NewsFooter`. One footer everywhere
 * (the shell renders it under every page).
 *
 * NO FEATURE LOSS — everything preserved from the prior band + NewsFooter:
 *   • newsletter signup (EXACT POST logic — same endpoint, `source: 'footer'`, states),
 *   • RSS + Atom + Breaking RSS feed links,
 *   • the full reachability grid (Read facets · Entities/Market/Social · Descout pages),
 *   • X social link, wordmark, © + Legal (reuses IPRightsModal).
 *
 * Mobile: the grid collapses (2-col → row at lg); the newsletter stacks on top.
 */

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { ArrowRight, Rss } from 'lucide-react';
import { XIcon } from '@/components/icons/SocialIcons';
import IPRightsModal from '@/components/modals/IPRightsModal';

interface FooterLink {
  href: string;
  label: string;
  external?: boolean;
}

/** Reachability grid — every orphan/long-tail page lives here. */
const LINK_COLUMNS: { title: string; links: FooterLink[] }[] = [
  {
    title: 'Read',
    links: [
      { href: '/read', label: 'All' },
      { href: '/breaking', label: 'Breaking' },
      { href: '/news', label: 'News' },
      { href: '/analysis', label: 'Analysis' },
      { href: '/guides', label: 'Guides' },
      { href: '/daily', label: 'Daily recap' },
      { href: '/weekly', label: 'Weekly recap' },
    ],
  },
  {
    // TODO(multichain): chain-scope these links — the `/tags` `/market` `/social`
    // standalone surfaces are retired, so Explore points at the primary chain's
    // in-shell views (same single-chain-stopgap routing as the global nav rail).
    title: 'Explore',
    links: [
      { href: '/abstract/entities', label: 'Entities' },
      { href: '/abstract/market', label: 'Market' },
      { href: '/abstract/social', label: 'Social' },
    ],
  },
  {
    title: 'Descout',
    links: [
      { href: '/about', label: 'About' },
      { href: '/submit', label: 'Submit an entity' },
      { href: '/subscribe', label: 'Subscribe' },
      { href: '/podcasts', label: 'Podcast' },
    ],
  },
  {
    title: 'Feeds',
    links: [
      { href: '/feed.xml', label: 'RSS', external: true },
      { href: '/atom.xml', label: 'Atom', external: true },
      { href: '/breaking/feed.xml', label: 'Breaking RSS', external: true },
    ],
  },
];

function FooterColumn({ title, links }: { title: string; links: FooterLink[] }) {
  return (
    <div className="flex flex-col gap-2.5">
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-ds">
        {title}
      </span>
      <nav className="flex flex-col gap-2">
        {links.map(({ href, label, external }) =>
          external ? (
            <a
              key={href}
              href={href}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.06em] text-parchment-dim transition-colors hover:text-scout-gold"
            >
              <Rss className="h-3 w-3" />
              {label}
            </a>
          ) : (
            <Link
              key={href}
              href={href}
              className="font-mono text-[11px] uppercase tracking-[0.06em] text-parchment-dim transition-colors hover:text-scout-gold"
            >
              {label}
            </Link>
          ),
        )}
      </nav>
    </div>
  );
}

export default function DashboardFooter() {
  const [isIPRightsModalOpen, setIsIPRightsModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  // OQ-SHAPE: the Open Questions board left the footer with this leg — it's an INDEX now, reachable
  // from each dossier's Open questions module + the /contribute doorway, not a standing nav link.
  const columns = LINK_COLUMNS;

  // EXACT newsletter POST logic from NewsFooter — same endpoint + source + states.
  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/newsletter/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          source: 'footer',
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSubscribed(true);
        setMessage(data.message);
        setEmail('');

        setTimeout(() => {
          setIsSubscribed(false);
          setMessage('');
        }, 5000);
      } else {
        setMessage(data.message);
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      setMessage('Failed to subscribe. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <footer className="border-t border-divider bg-ink">
        {/* Main band: link grid + newsletter. Stacks on mobile, row at lg. */}
        <div className="mx-auto flex max-w-screen-2xl flex-col gap-8 px-4 py-9 sm:px-6 lg:flex-row lg:items-start lg:justify-between lg:px-8">
          {/* Identity + reachability grid */}
          <div className="flex flex-col gap-6">
            {/* Inside the product, "home" = the desk (the primary hub), NOT the `/`
                marketing homepage — the old `/`→`/abstract` 307 is gone (Lam, 07-09). */}
            <Link
              href="/abstract/"
              className="flex flex-shrink-0 items-center"
              aria-label="Descout — the desk"
            >
              <Image
                src="/descout-logo-dark.png"
                alt="Descout"
                width={120}
                height={20}
                className="hidden h-4 w-[96px] dark:block"
              />
              <Image
                src="/descout-logo-light.png"
                alt="Descout"
                width={120}
                height={20}
                className="block h-4 w-[96px] dark:hidden"
              />
            </Link>

            <div className="grid grid-cols-2 gap-x-10 gap-y-7 sm:grid-cols-4">
              {columns.map((col) => (
                <FooterColumn key={col.title} title={col.title} links={col.links} />
              ))}
            </div>
          </div>

          {/* Newsletter — "follow Descout" */}
          <div className="flex max-w-xs flex-shrink-0 flex-col gap-2.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-ds">
              Follow Descout
            </span>
            <p className="font-mono text-[11px] leading-relaxed text-parchment-dim">
              The newsroom brief in your inbox. No algorithm, no tracking.
            </p>
            {isSubscribed ? (
              <p className="font-mono text-[11px] text-signal-green">
                {message || 'Subscribed — check your inbox.'}
              </p>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="flex items-center gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email for updates"
                  aria-label="Email for the newsletter"
                  required
                  disabled={isLoading}
                  className="w-44 rounded border-[0.5px] border-divider bg-surface px-3 py-1.5 font-mono text-[12px] text-parchment outline-none transition-colors placeholder:text-parchment-dim focus:border-scout-gold focus:ring-1 focus:ring-scout-gold disabled:opacity-50 sm:w-52"
                />
                <button
                  type="submit"
                  disabled={isLoading || !email.trim()}
                  className="inline-flex flex-shrink-0 items-center gap-1.5 rounded bg-scout-gold px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.06em] text-ink transition-colors hover:bg-scout-gold/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? 'Sending…' : (
                    <>
                      Subscribe <ArrowRight className="h-3 w-3" />
                    </>
                  )}
                </button>
              </form>
            )}
            {message && !isSubscribed && (
              <p className="mt-0.5 font-mono text-[10px] text-alert-red">{message}</p>
            )}
          </div>
        </div>

        {/* Bottom rule: © + Legal + X. */}
        <div className="border-t border-divider">
          <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 sm:px-6 lg:px-8">
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-parchment-dim">
              © {new Date().getFullYear()} Descout
            </span>
            <button
              type="button"
              onClick={() => setIsIPRightsModalOpen(true)}
              className="cursor-pointer border-none bg-transparent font-mono text-[10px] uppercase tracking-[0.1em] text-parchment-dim transition-colors hover:text-parchment"
            >
              Legal
            </button>
            <span className="flex-1" />
            <a
              href="https://x.com/descoutxyz"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Descout on X"
              className="inline-flex items-center justify-center text-parchment-dim transition-colors hover:text-scout-gold"
            >
              <XIcon className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </footer>

      <IPRightsModal
        isOpen={isIPRightsModalOpen}
        onClose={() => setIsIPRightsModalOpen(false)}
      />
    </>
  );
}
